

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { MessagePlugin } from 'tdesign-react';

import { IBot, NarrationType } from '@/interface';
import { fetchBotDetail, fetchBotList } from '@/services/bot';
import { CgiErrorCode } from '@/services/cgi';

export interface BotState {
  botList?: IBot[];
  botInfo: Partial<IBot> | null;
  loading: boolean;
  disabled: boolean;
  startTime?: number;
  clearTime?: number;
}

export const initialState: BotState = {
  botList: [],
  botInfo: {
    name: '未命名',
    intro: '',
    talking: '',
    narrationType: NarrationType.NARRATION_TYPE_DEFAULT,
    ignoreMemory: 0,
    visibility: 3,
    phasesNew: [{ character: '' }],
    scoreRules: [],
    initScore: 0,
    phaseSelectors: [],
  },
  disabled: true,
  loading: false,
};

export const getBotDetail = createAsyncThunk('bot/detail', fetchBotDetail);
export const getBotList = createAsyncThunk('bot/list', fetchBotList);

export const bot = createSlice({
  name: 'bot',

  initialState,

  reducers: {
    clearBot() {
      return initialState;
    },
    setBotLoading(state, action) {
      state.loading = action.payload;
    },
    setDisabled(state, { payload }: PayloadAction<boolean>) {
      state.disabled = payload;
    },
    setStartTime(state, { payload }: PayloadAction<number>) {
      state.startTime = payload;
    },
    createNewBot(state) {
      state.botInfo = initialState.botInfo;
      state.disabled = false;
      state.clearTime = dayjs().valueOf();
    },
    updateBotInfo(state, { payload }: PayloadAction<Partial<IBot>>) {
      state.botInfo = {
        ...state.botInfo, ...payload,
      };
    },
    clearBotInfo(state) {
      state.botInfo = initialState.botInfo;
      state.disabled = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBotDetail.fulfilled, (state, { payload }) => {
      if (!payload) {
        return initialState;
      }
      const { code, data, message } = payload;
      if (code === CgiErrorCode.NO_ERROR) {
        return {
          ...state,
          loading: false,
          botInfo: {
            ...data,
            name: data?.name || '未命名',
            phasesNew: data?.phasesNew || [{ character: '' }],
            phaseSelectors: data?.phaseSelectors || [],
            scoreRules: data?.scoreRules || [],
            initScore: data?.initScore || 0,
            ignoreMemory: data?.ignoreMemory === undefined ? 0 : data.ignoreMemory,
            visibility: data?.visibility || 3,
          },
        };
      }
      MessagePlugin.error(message || '请求失败，请稍后重试');
    });

    builder.addCase(getBotList.fulfilled, (state, { payload }) => {
      state.botList = payload.list;
    });
  },
});

export const { clearBot, createNewBot, setBotLoading, updateBotInfo, clearBotInfo, setStartTime,
  setDisabled } = bot.actions;

export default bot.reducer;
